import LogRocket from 'logrocket'
import React, { useCallback, useContext, useEffect } from 'react';

import { Collections } from 'Collections';
import { analytics } from 'FirebaseConfigs';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { AppRoles } from 'models/AppRoles';
import { DbDoc, WithRef } from 'models/FbDoc';
import { OrgUser, orgUserIdFrom } from 'models/OrgUser';
import { sortAlphabetically } from 'Util/sortAlphabetically';

import { useAppUser } from './AppUserProvider';
import { useOrg, useUnselectOrg } from './OrgProvider';
import { RunAfterTimer } from './RunAfterTimer';
import { Snacbars } from './Snacbars';

type Props = {
    children: React.ReactNode
}

const ORG_USER_CONTEXT = React.createContext<[orgUser: WithRef<DbDoc<OrgUser>> | null, loading: boolean]>([null, true])

export function useOrgUser() {
    return useContext(ORG_USER_CONTEXT)
}

export function OrgUserProvider({ children }: Props) {
    const [org, loadingOrg] = useOrg()
    const [unselectOrg] = useUnselectOrg()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUser, loadingOrgUser, error] = FirebaseUtils.useDocumentData<OrgUser>(
        true,
        Collections.ORG_USERS,
        org && appUser && orgUserIdFrom(org?.data.id, appUser?.data.id),
        AnalyticsEvent.FETCH_ORG_USER_ERROR
    )

    const loading = loadingOrg || loadingOrgUser || loadingAppUser

    useEffect(() => {
        if (orgUser) {
            const orgRolesString = orgUser?.data.orgRoles ? Object.keys(orgUser?.data.orgRoles).sort(sortAlphabetically).join(',') : ''
            analytics.setUserProperties({ orgUserRoles: orgRolesString })
            LogRocket.identify(orgUser.data.userId, {
                orgUserName: orgUser?.data.orgContactInfo?.name?.fullName || '',
                orgUserEmail: orgUser.data.orgContactInfo?.email?.email || '',
                orgUserPhone: orgUser.data.orgContactInfo?.phone?.phoneNumberString || '',
                orgRolesString,
            })
        }
    }, [orgUser])

    return (
        <ORG_USER_CONTEXT.Provider value={[orgUser || null, loading]}>
            <Snacbars error={error?.message} />
            <RunAfterTimer
                time={5000}
                check={!orgUser && !loading && !!appUser && !appUser.data.appRoles.map[AppRoles.FlatEnum.ADMIN]}
                run={useCallback(() => {
                    analytics.logEvent(AnalyticsEvent.MISSING_ORG_USER)
                    unselectOrg(AnalyticsEvent.MISSING_ORG_USER)
                }, [unselectOrg])} />
            {children}
        </ORG_USER_CONTEXT.Provider>
    )
}
